import { lazy, Suspense, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  useParams,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import PronunciationGuide from './pronunciation/PronunciationGuide';
import Bhajans from './components/Bhajans';
import BhajanDetails from './components/BhajanDetails';
import Distraction from './components/Distraction';
import Collections from './components/Collections';
import Playlist from './components/Playlist';
import BhajanBabies from './components/BhajanBabies';
import Browse from './components/Browse';
import Download from './components/Download';
import MetadataBrowseList from './components/MetadataBrowseList';
import { Header } from './components/layout/Header';
import { APP } from './app/appConfig';
import { SCREENS } from './app/screens';
import { BROWSE_FACETS } from './browse/facets';
import { RootState } from './store/store';
import { updateLanguageAPI } from './services/api';
import { setDisplayLanguage } from './utils/displayLanguage';
import { AudioDock } from './audio/AudioDock';
import { ProgressBar, getProgress, ToastProvider } from './ui';

// Lazy chunk so OSMD + the engine never load on ordinary pages. With the flag
// off the route below is dead-code-eliminated; webpack still EMITS the chunk
// file (import() is statically analyzed) but nothing ever references or
// fetches it.
const WesternNotesViewer = lazy(() => import('./sheetMusic/WesternNotesViewer'));

// Standalone feature-story pages under /discover — unlinked from nav; the URL is
// what gets shared. Lazy so their fetch-and-render code never loads on ordinary
// pages. Content is data (public/discover/**), authored by the night-pr pipeline.
const DiscoverIndex = lazy(() => import('./discover/DiscoverIndex'));
const DiscoverPage = lazy(() => import('./discover/DiscoverPage'));

// /history — the 2011 app, reconstructed. Unlinked like /discover, and lazy so
// its stylesheet and its lyric-sheet scan never load on ordinary pages.
const AppHistory = lazy(() => import('./history/AppHistory'));

function RouteProgress() {
  const loc = useLocation();
  useEffect(() => {
    getProgress().start();
    const t = window.setTimeout(() => getProgress().done(), 350);
    return () => window.clearTimeout(t);
  }, [loc.pathname]);
  return null;
}

function ScrollToTop() {
  const loc = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [loc.pathname]);
  return null;
}

/** Backwards-compat: /bhajan/node/:id/:lang -> /nodes/:id (drops the language). */
function LegacyBhajanRedirect() {
  const { nodeId } = useParams<{ nodeId: string; language: string }>();
  return <Navigate to={`/nodes/${nodeId}`} replace />;
}

/** Backwards-compat: /distraction/node/:id/:lang -> /distraction/nodes/:id. */
function LegacyDistractionRedirect() {
  const { nodeId } = useParams<{ nodeId: string; language: string }>();
  return <Navigate to={`/distraction/nodes/${nodeId}`} replace />;
}

function App() {
  const language = useSelector((state: RootState) => state.language.selectedLanguage);

  // Set request + display language synchronously during render so the FIRST
  // catalog fetch on a cold load (with a persisted non-English language) uses
  // the right language — child fetch effects run before a parent useEffect would.
  // Both are guarded module-singleton setters, safe to call on every render.
  updateLanguageAPI(language);
  setDisplayLanguage(language);

  return (
    <Router>
      <ToastProvider>
        <ProgressBar />
        <RouteProgress />
        <ScrollToTop />
        <Header />
        <Routes>
          <Route path="/" element={<SCREENS.Home />} />
          <Route path="/about" element={<SCREENS.About />} />
          {APP.features.pronunciation && (
            <Route path="/pronunciation" element={<PronunciationGuide />} />
          )}
          {BROWSE_FACETS.length > 0 && (
            <Route path="/browse" element={<Browse />} />
          )}
          {BROWSE_FACETS.length > 0 && (
            <Route path="/browse/:facet" element={<MetadataBrowseList />} />
          )}
          <Route path="/bhajans" element={<Bhajans language={language} />} />
          <Route path="/contact" element={<SCREENS.Contact />} />
          {APP.features.appDownload && (
            <Route path="/download" element={<Download />} />
          )}

          {/* Unlinked feature-story hub + per-feature pages (night-pr). */}
          <Route
            path="/discover"
            element={
              <Suspense fallback={null}>
                <DiscoverIndex />
              </Suspense>
            }
          />
          <Route
            path="/discover/:slug"
            element={
              <Suspense fallback={null}>
                <DiscoverPage />
              </Suspense>
            }
          />

          {/* Unlinked: the 2011 app, recovered from its own .ipa. */}
          {APP.features.appHistory && (
            <Route
              path="/history"
              element={
                <Suspense fallback={null}>
                  <AppHistory />
                </Suspense>
              }
            />
          )}

          {/* Canonical share/deep-link URLs — match bhajanmala.com */}
          <Route path="/nodes/:nodeId" element={<BhajanDetails />} />
          {/* The viewer stays a lazy chunk: it carries OSMD, the transport and
              the soundfont loader, and only this route needs them. */}
          <Route
            path="/bhajan/:id/western-notes"
            element={
              <Suspense fallback={null}>
                <WesternNotesViewer />
              </Suspense>
            }
          />
          {APP.features.distraction && (
            <Route path="/distraction/nodes/:nodeId" element={<Distraction />} />
          )}
          {APP.features.collections && (
            <Route path="/collections" element={<Collections />} />
          )}
          {APP.features.collections && (
            <Route path="/collections/:nodeId" element={<Collections />} />
          )}
          {APP.features.playlists && (
            <Route path="/playlists/:playlistId" element={<Playlist />} />
          )}
          {APP.features.bhajanBabiesSection && (
            <Route path="/bhajan-babies" element={<BhajanBabies />} />
          )}

          {/* Legacy URLs — redirect to canonical */}
          <Route path="/bhajan/node/:nodeId/:language" element={<LegacyBhajanRedirect />} />
          <Route path="/distraction/node/:nodeId/:language" element={<LegacyDistractionRedirect />} />

          {/* bm leaves unknown paths unmatched, exactly as it always has. */}
          {APP.features.redirectUnknownToHome && (
            <Route path="*" element={<Navigate to="/" replace />} />
          )}
        </Routes>
        <AudioDock />
      </ToastProvider>
    </Router>
  );
}

export default App;
